import { AfterViewChecked, Component, OnInit } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyPipe, DatePipe, DecimalPipe, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { PostComponent } from '../post/post.component';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { RedirecFormService } from 'src/app/services/redirec-form.service';
import { SocketioService } from 'src/app/services/socket/socketio.service';
import { LocationServiceService } from '../../services/location-service.service';
import { FeeService } from 'src/app/services/fee.service';
import { WidgetUserProxy } from 'src/app/services/widget-user-proxy.service';
import { WidgetPostService } from 'src/app/services/widget-post.service';

/**
 * WidgetPostComponent
 *
 * Extends PostComponent to be used as an embeddable widget (iframe) from the CRM.
 * - Authenticated via widget JWT (not user session)
 * - All API calls routed through widget proxy endpoints
 * - CRM metadata (analyst, caseId) injected into payment registration
 * - No edit mode, no navigation — shows confirmation then resets
 */
@Component({
  selector: 'app-widget-post',
  templateUrl: '../post/post.component.html',
  styleUrls: ['../post/post.component.css', './widget-post.component.css'],
  providers: [
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    WidgetPostService,
    { provide: UserService, useClass: WidgetUserProxy },
    // Mock services not needed in widget context
    { provide: AuthService, useValue: { sessionUser: null } },
    { provide: SocketioService, useValue: {} },
    { provide: RedirecFormService, useValue: {} },
    { provide: LocationServiceService, useValue: { getPosition: () => Promise.resolve({ lat: 0, lng: 0 }) } },
  ]
})
export class WidgetPostComponent extends PostComponent implements AfterViewChecked {

  private widgetToken = '';
  private widgetPersonId = '';

  constructor(
    offcanvasService: NgbOffcanvas,
    decimalPipe: DecimalPipe,
    currencyPipe: CurrencyPipe,
    router: Router,
    route: ActivatedRoute,
    _auth: AuthService,
    formBuilder: FormBuilder,
    activatedRoute: ActivatedRoute,
    _user: UserService,
    sanitizer: DomSanitizer,
    redirecFormService: RedirecFormService,
    socketService: SocketioService,
    datePipe: DatePipe,
    location: Location,
    locationService: LocationServiceService,
    feeService: FeeService
  ) {
    super(
      offcanvasService, decimalPipe, currencyPipe, router, route,
      _auth, formBuilder, activatedRoute, _user, sanitizer,
      redirecFormService, socketService, datePipe, location,
      locationService, feeService
    );

    // Configure the proxy BEFORE ngOnInit runs
    // Note: _user is the constructor param (local), not this._user (private in parent)
    this.widgetPersonId = route.snapshot.paramMap.get('code_person') || '';
    this.widgetToken = route.snapshot.queryParamMap.get('token') || '';
    (_user as unknown as WidgetUserProxy).configureWidget(this.widgetPersonId, this.widgetToken);
    this.isWidgetMode = true;
  }

  /**
   * Override: After payment is registered, don't navigate away.
   * The widget stays in selectView=4 (confirmation) which already shows
   * success/failure. User can then close/reload the widget from the CRM.
   */
  override redirectTo() {
    // In widget mode, reset form instead of navigating
    this.selectView = 1;
    this.confirm = false;
    this.confirmLoading = false;
  }

  /**
   * Override: Don't navigate away when no params
   * (the parent's ngOnInit calls router.navigate(['/']) for invalid params)
   */
  override goBack() {
    // No-op in widget mode
  }

  /**
   * Iframe auto-resize: post height to parent window
   */
  ngAfterViewChecked(): void {
    try {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'widget-resize', height }, '*');
    } catch (e) {
      // Silently ignore if not in iframe
    }
  }
}
